import { useEffect, useMemo, useState } from "react";
import { ShieldAlert, Search } from "lucide-react";
import { api } from "@/lib/api";

const EVENT_LABELS = {
  login_success: "Login successful",
  login_failed: "Login failed",
  password_verified_mfa_required: "Password verified - MFA required",
  mfa_failed: "MFA verification failed",
  mfa_enabled: "MFA enabled",
  mfa_recovery_code_used: "Recovery code used",
  mfa_recovery_codes_regenerated: "Recovery codes regenerated",
  mfa_recovery_codes_regeneration_failed: "Recovery code regeneration failed",
  mfa_replacement_verification_failed: "MFA replacement verification failed",
  mfa_replacement_started: "MFA replacement started",
  mfa_replacement_completed: "MFA replacement completed",
  break_glass_mfa_reset_requested: "Break-glass MFA recovery initiated",
  break_glass_password_reset_requested: "Break-glass password recovery initiated",
  logout: "Signed out",
  session_expired: "Session expired",
  session_revoked: "Session revoked",
  other_sessions_revoked: "Other sessions revoked",
  admin_user_created: "Admin user created",
  admin_user_invitation_sent: "Portal invitation sent",
  admin_user_invitation_failed: "Portal invitation failed",
  admin_user_status_changed: "Admin user status changed",
  admin_user_archived: "Admin user archived",
  admin_user_restored: "Admin user restored",
  admin_user_sessions_revoked: "Admin user sessions revoked",
  admin_user_unlocked: "Account unlocked",
  login_blocked: "Login blocked",
  account_access_blocked: "Account access blocked",
};

export default function AdminSecurityLogs() {
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [eventType, setEventType] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await api.get("/auth/security-events");

        if (active) {
          setSecurityEvents(data || []);
        }
      } catch {
        if (active) {
          setSecurityEvents([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);


  const filteredEvents = useMemo(() => {
    const query = search.trim().toLowerCase();

    return securityEvents.filter((event) => {
      if (eventType !== "all" && event.event_type !== eventType) {
        return false;
      }

      if (outcome !== "all" && event.outcome !== outcome) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = [
        EVENT_LABELS[event.event_type] || event.event_type,
        event.event_type,
        event.outcome,
        event.email,
        event.ip,
        event.user_agent,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [securityEvents, eventType, outcome, search]);

  const eventTypes = [...new Set(
    securityEvents
      .map((event) => event.event_type)
      .filter(Boolean)
  )].sort();

  return (
    <div className="max-w-5xl" data-testid="page-admin-security-logs">
      <div className="flex items-center gap-3">
        <div className="rounded-sm bg-navy p-2.5 text-cyan">
          <ShieldAlert size={22} />
        </div>

        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">
            Security Logs
          </h1>

          <p className="mt-1 text-sm text-slate">
            Review recent authentication and account-security events.
          </p>
        </div>
      </div>

      <section className="mt-8 rounded-md border border-brandborder bg-white p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate mb-2">
              Event type
            </label>

            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full rounded-sm border border-brandborder bg-white px-3 py-2.5 text-sm text-navy"
            >
              <option value="all">All events</option>

              {eventTypes.map((type) => (
                <option key={type} value={type}>
                  {EVENT_LABELS[type] || type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate mb-2">
              Outcome
            </label>

            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              className="w-full rounded-sm border border-brandborder bg-white px-3 py-2.5 text-sm text-navy"
            >
              <option value="all">All outcomes</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate mb-2">
              Search
            </label>

            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate"
              />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="IP, browser or event"
                className="w-full rounded-sm border border-brandborder bg-white pl-9 pr-3 py-2.5 text-sm text-navy"
              />
            </div>
          </div>
        </div>

        <div className="mt-5 text-xs text-slate">
          Showing {filteredEvents.length} of {securityEvents.length} events
        </div>

        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="text-sm text-slate">
              Loading security logsâ€¦
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-sm text-slate">
              No matching security events.
            </div>
          ) : (
            filteredEvents.map((event) => (
              <div
                key={event.id}
                className="rounded-sm border border-brandborder bg-lightbg p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-navy">
                      {EVENT_LABELS[event.event_type] || event.event_type}
                    </div>

                    <div className="mt-1 text-xs text-slate">
                      {event.created_at
                        ? new Date(event.created_at).toLocaleString("en-GB")
                        : ""}
                    </div>
                  </div>

                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      event.outcome === "success"
                        ? "bg-emerald-50 text-emerald-700"
                        : event.outcome === "failed"
                          ? "bg-rose-50 text-rose-700"
                          : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {event.outcome || "unknown"}
                  </span>
                </div>

                <div className="mt-3 grid gap-1 text-xs text-slate">
                  {event.email && (
                    <div>
                      <span className="font-semibold text-navy">
                        Account:
                      </span>{" "}
                      {event.email}
                    </div>
                  )}

                  {event.ip && (
                    <div>
                      <span className="font-semibold text-navy">
                        IP:
                      </span>{" "}
                      {event.ip}
                    </div>
                  )}

                  {event.user_agent && (
                    <div className="break-words">
                      <span className="font-semibold text-navy">
                        Device:
                      </span>{" "}
                      {event.user_agent}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

